import { type DrivingGroup, type EcoEventRow, type EcoResult } from "@fts/core";
import { Suspense, lazy, useState } from "react";
import { EmptyState, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { formatDateTime } from "../../lib/format";
import { drivingRowLabel } from "../../lib/report-sheets";
import { useSession } from "../../state/session";
import { ChartPanel, SummaryCards, previousOf } from "./ReportParts";
import { toSummaryLines } from "./presenters";
import { ExportBar, Loading, settleWithin, useLatest, useLoad, PREVIOUS_WAIT_MS, type ExportSpec } from "./shared";
import { ecoChart, ecoSheets, ecoSummary } from "./telematics";

const EcoMap = lazy(() => import("./EcoMap"));

/** Rows of the event list shown before "show all". */
const LIST_ROWS = 50;

/**
 * Eco-driving events in the driving report (ADR 0034): harsh acceleration, braking and cornering from the
 * accelerometer of Teltonika units, per vehicle / operator, as a list and on the map. The source is labelled:
 * m-DVR and our own unit do not measure these events, so their vehicles are listed as "not measured" and
 * must not be compared with a zero. Same KVKK rules as the driving rows; exports logged.
 */
export function EcoSection({
  query,
  group,
  filters,
  period,
  from,
  to,
}: {
  query: { tenantId: string; siteId: string | null; vehicleIds: string[]; operatorId: string | null } | null;
  group: DrivingGroup;
  filters: [string, string][];
  period: string;
  from: string;
  to: string;
}) {
  const { t, locale, lang } = useI18n();
  const { provider } = useSession();
  const [all, setAll] = useState(false);
  const [mapError, setMapError] = useState(false);
  const q = (range = { from, to }) => ({ ...query!, ...range, group });
  const deps = [provider, query?.tenantId, query?.siteId, query?.vehicleIds.join(","), query?.operatorId, from, to, group];
  const state = useLoad<EcoResult>(query ? () => provider.reportEco(q()) : null, deps);
  const prevRange = previousOf({ from, to });
  const prev = useLoad<EcoResult>(query && state.status === "ready" ? () => provider.reportEco(q(prevRange)) : null, [...deps, state.status, state.data]);
  const prevLatest = useLatest(prev);
  const res = state.data;
  const ctx = { t, locale, lang };
  const measured = res?.sources.filter((s) => s.eco) ?? [];
  const notMeasured = res?.sources.filter((s) => !s.eco) ?? [];
  const label = (e: EcoEventRow) => `${t(`eco.type.${e.type}`)} · ${e.fleetNo}${e.g !== null ? ` · ${e.g.toLocaleString(locale, { maximumFractionDigits: 2 })} g` : ""}`;

  const spec: ExportSpec | null = state.status === "ready" && res && query
    ? {
        report: "eco",
        reportName: `${t("eco.title")} — ${t(`reports.driving.group.${group}`)}`,
        slug: t("eco.slug"),
        part: `${group}_${from}_${to}`,
        tz: res.tz,
        filters: [[t("reports.driving.group"), t(`reports.driving.group.${group}`)], ...filters, [t("eco.source"), t("eco.sourceValue", { measured: measured.length, total: res.sources.length })]],
        period,
        rows: res.rows.length + res.events.length + 1,
        load: async (format) => {
          const [r, p] = await Promise.all([provider.exportEco(q(), format), format === "pdf" ? settleWithin(prevLatest.current.promise, PREVIOUS_WAIT_MS) : Promise.resolve(null)]);
          return { sheets: ecoSheets(t, r), tz: r.tz, summary: toSummaryLines(ctx, ecoSummary(ctx, r, p)), chart: { title: t("eco.chart.title"), spec: ecoChart(ctx, r), note: t("eco.sourceNote") } };
        },
      }
    : null;

  return (
    <section className="report-section" aria-labelledby="eco-title">
      <h2 id="eco-title" className="section-title">
        {t("eco.title")}
      </h2>
      <p className="info-note">
        <strong>{t("eco.source")}:</strong> {res ? t("eco.sourceValue", { measured: measured.length, total: res.sources.length }) : t("eco.sourceShort")} {t("eco.sourceNote")}
        {notMeasured.length > 0 && <> {t("eco.notMeasured", { vehicles: notMeasured.slice(0, 8).map((s) => s.fleetNo).join(", ") + (notMeasured.length > 8 ? "…" : "") })}</>}
      </p>
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.status === "loading" || state.status === "idle" ? (
        query ? <Loading /> : null
      ) : !res || measured.length === 0 ? (
        state.status === "error" ? null : <EmptyState compact icon="info" title={t("eco.noSource")}>{t("eco.noSourceBody")}</EmptyState>
      ) : (
        <>
          <SummaryCards items={ecoSummary(ctx, res, prev.status === "ready" ? prev.data : null)} busy={prev.status === "loading"} prevFrom={prevRange.from} prevTo={prevRange.to} />
          <ExportBar spec={spec} disabled={res.totals.total === 0} />
          {res.totals.total === 0 ? (
            <EmptyState compact icon="info" title={t("eco.none")} />
          ) : (
            <>
              <ChartPanel title={t("eco.chart.title")} spec={ecoChart(ctx, res)} note={t("eco.sourceNote")} />
              <Panel flush title={t("eco.table.title")}>
                <div className="table-wrap table-scroll">
                  <table className="table report-table">
                    <thead>
                      <tr>
                        <th scope="col">{group === "operator" ? t("reports.col.operator") : t("reports.col.vehicle")}</th>
                        <th scope="col" className="num">{t("eco.type.harsh_accel")}</th>
                        <th scope="col" className="num">{t("eco.type.harsh_brake")}</th>
                        <th scope="col" className="num">{t("eco.type.harsh_corner")}</th>
                        <th scope="col" className="num">{t("eco.col.total")}</th>
                        <th scope="col" className="num col-hide-sm">{t("eco.col.maxG")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.rows.map((r) => (
                        <tr key={`${r.kind}:${r.id ?? ""}`}>
                          <td>{r.kind === "operator" || r.kind === "vehicle" ? r.name : <span className="muted">{drivingRowLabel(t, r)}</span>}</td>
                          {r.suppressed ? (
                            <td className="muted" colSpan={5}>
                              {t("reports.driving.suppressed")}
                            </td>
                          ) : (
                            <>
                              <td className="num">{r.accel}</td>
                              <td className="num">{r.brake}</td>
                              <td className="num">{r.corner}</td>
                              <td className="num">{r.total}</td>
                              <td className="num col-hide-sm">{r.maxG === null ? t("common.empty") : r.maxG.toLocaleString(locale, { maximumFractionDigits: 2 })}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
              <Panel title={t("eco.map.title")} flush>
                {mapError ? (
                  <EmptyState compact icon="alert" title={t("reports.spaghetti.mapError")} />
                ) : (
                  <Suspense fallback={<Loading shape="block" />}>
                    <EcoMap events={res.events} label={t("eco.map.label")} title={label} onError={() => setMapError(true)} />
                  </Suspense>
                )}
              </Panel>
              <Panel flush title={t("eco.list.title")} meta={<span>{t("reports.rows", { count: res.events.length })}</span>}>
                <div className="table-wrap table-scroll">
                  <table className="table report-table">
                    <thead>
                      <tr>
                        <th scope="col">{t("eco.col.at")}</th>
                        <th scope="col">{t("reports.col.vehicle")}</th>
                        <th scope="col">{t("eco.col.type")}</th>
                        <th scope="col" className="num">{t("eco.col.g")}</th>
                        <th scope="col" className="col-hide-sm">{t("reports.col.operator")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(all ? res.events : res.events.slice(0, LIST_ROWS)).map((e) => (
                        <tr key={`${e.vehicleId}:${e.at}:${e.type}`}>
                          <td className="nowrap">{formatDateTime(e.at, locale)}</td>
                          <td className="mono">{e.fleetNo}</td>
                          <td>{t(`eco.type.${e.type}`)}</td>
                          <td className="num">{e.g === null ? t("common.empty") : e.g.toLocaleString(locale, { maximumFractionDigits: 2 })}</td>
                          <td className="col-hide-sm">{e.operatorName ?? <span className="muted">{t("common.empty")}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!all && res.events.length > LIST_ROWS && (
                  <p className="panel-text">
                    <button type="button" className="button button-secondary button-sm" onClick={() => setAll(true)}>
                      {t("eco.list.showAll", { count: res.events.length })}
                    </button>
                  </p>
                )}
                {res.truncated && <p className="panel-text muted">{t("eco.list.truncated", { max: res.events.length })}</p>}
                {res.hiddenEvents > 0 && <p className="panel-text muted">{t("eco.list.hidden", { count: res.hiddenEvents })}</p>}
              </Panel>
            </>
          )}
        </>
      )}
    </section>
  );
}
